import styles from './CompanyDetails.module.css';
import StripeIcon from './stripe.svg?react';
import {
  Alert,
  Input,
  Form,
  Card,
  message,
  Flex,
  Checkbox,
  Modal,
  Space,
  Button,
  DatePicker,
  Popconfirm,
  Typography,
  Divider,
} from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import {
  handleErrorInputMessage,
  handleInputError,
} from '../../../core/utils/errorUtils';
import {
  FormCard,
  FormContainer,
  FormLabelWithHelp,
  FormSectionTitle,
  READ_ONLY_INPUT_STYLE,
} from '@leedpulse-monorepo/components';
import { billingIntervalAsString, ContextStatus, UserRole } from '@leedpulse-monorepo/enums';
import CompanyEditionFormContext from '../../contexts/data/companies/CompanyEditionFormContext';
import { COMPANIES_PAGE_PATH } from '../../pages/companies/CompaniesPage';
import linkStripeSubscription from '../../../domain/use_cases/companies/linkStripeSubscription';
import setOnboardingDate, { CommitmentMove } from '../../../domain/use_cases/companies/setOnboardingDate';
import unlockOnboarding from '../../../domain/use_cases/companies/unlockOnboarding';
import getUnpaidInvoices, { UnpaidInvoice } from '../../../domain/use_cases/companies/getUnpaidInvoices';
import markInvoicePaidOutOfBand from '../../../domain/use_cases/companies/markInvoicePaidOutOfBand';
import CompaniesContext from '../../contexts/data/companies/CompaniesContext';
import { EditOutlined, ExportOutlined, LinkOutlined, PlusOutlined, UnlockOutlined } from '@ant-design/icons';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import DemoAccountForm from './DemoAccount/DemoAccountForm';
import dayjs from 'dayjs';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';

interface IExtendedCompany extends ICompany {
  customer_url?: string;
  last_paid_invoice_url?: string;
  last_paid_invoice_id?: string;
}

const CompanyDetails: React.FC = () => {
  const navigate = useNavigate();
  const companyEditionFormContext = useContext(CompanyEditionFormContext);
  const companiesContext = useContext(CompaniesContext);
  const authContext = useContext(AuthenticationContext);

  const companyDoc = companiesContext.selected as ICompany;

  // Modals
  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);

  const [newCustomerId, setNewCustomerId] = useState('');
  const [deleteOldCustomer, setDeleteOldCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditingIdStripe, setIsEditingIdStripe] = useState(false);

  // Carries the company it was typed for: untouched, or typed for another company, the field
  // falls back to what is stored. Without that, "Appliquer" on a field nobody touched sent null
  // and dropped the date on screen.
  const [onboardingDraft, setOnboardingDraft] = useState<{ companyId: string; value: dayjs.Dayjs | null } | null>(null);
  const [isSavingOnboarding, setIsSavingOnboarding] = useState(false);
  const [isUnlockingOnboarding, setIsUnlockingOnboarding] = useState(false);

  // The engagement moves the date is about to cause, and the corrections made on them. Nothing
  // is written while this is open: a re-onboarding on a company served for months would
  // otherwise push every engagement by the same months, silently.
  const [onboardingPreview, setOnboardingPreview] = useState<{ onboardingDate: number | null; moves: CommitmentMove[] } | null>(null);
  const [commitmentEdits, setCommitmentEdits] = useState<Record<string, number | null>>({});

  // Carries the company it was fetched for, so switching companies never shows the previous one's
  // invoices while the new fetch is in flight
  const [unpaidInvoices, setUnpaidInvoices] = useState<{ companyId: string; invoices: UnpaidInvoice[] } | null>(null);
  const [settlingInvoiceId, setSettlingInvoiceId] = useState<string | null>(null);

  const [isCreateDemoAccountModalOpen, setIsCreateDemoAccountModalOpen] = useState(false);
  const isDemoAccount = useMemo(() => companyDoc && (companyDoc as ICompany).id === "company-lf-4B8ID86YYO", [companyDoc]);

  const handleOpenStripeModal = () => {
    setNewCustomerId('');
    setDeleteOldCustomer(false);
    setIsStripeModalOpen(true);
  };

  const toggleCreateDemoAccountModal = () => {
    setIsCreateDemoAccountModalOpen(o => !o);
  };

  /**
   * Ask the server where each engagement would land, and show it. Nothing is written yet.
   */
  const handlePreviewOnboardingDate = async () => {
    if (!companyDoc || !authContext.authentication) return;

    setIsSavingOnboarding(true);
    try {
      const result = await setOnboardingDate(
        {
          company_id: companyDoc.id,
          onboarding_date: onboardingValue ? onboardingValue.format('YYYY-MM-DD') : null,
          preview: true,
        },
        authContext.authentication
      );

      setCommitmentEdits({});
      setOnboardingPreview({ onboardingDate: result.onboarding_date, moves: result.commitments ?? [] });
    } catch (e: unknown) {
      message.error((e as { message?: string })?.message || 'Une erreur est survenue');
    }
    setIsSavingOnboarding(false);
  };

  /**
   * Writing the date moves the paid period so it starts that day, and every engagement with it.
   * The service follows: it waits when the day is ahead, it opens when it has come.
   *
   * The engagements are sent explicitly, as shown in the recap: the server recomputes nothing.
   */
  const handleConfirmOnboardingDate = async () => {
    if (!companyDoc || !authContext.authentication || !onboardingPreview) return;

    setIsSavingOnboarding(true);
    try {
      const result = await setOnboardingDate(
        {
          company_id: companyDoc.id,
          onboarding_date: onboardingValue ? onboardingValue.format('YYYY-MM-DD') : null,
          commitments: Object.fromEntries(onboardingPreview.moves.map((oMove) => [
            oMove.subscription_id,
            oMove.subscription_id in commitmentEdits ? commitmentEdits[oMove.subscription_id] : oMove.proposed,
          ])),
        },
        authContext.authentication
      );

      message.success(
        result.onboarding_date
          ? `Onboarding au ${dayjs(result.onboarding_date).format('DD/MM/YYYY')}${result.next_billing_date ? `, prochaine facturation le ${dayjs(result.next_billing_date).format('DD/MM/YYYY')}` : ''}`
          : 'Date retirée'
      );

      setOnboardingPreview(null);
      companiesContext.fetchAndSelectById(companyDoc.id);
    } catch (e: unknown) {
      message.error((e as { message?: string })?.message || 'Une erreur est survenue');
    }
    setIsSavingOnboarding(false);
  };

  /**
   * Re-open a date the onboarding has closed: the field goes back to writable and empty.
   *
   * Nothing moves here — it is "Appliquer" that suspends the collection and shifts the cycle.
   */
  const handleUnlockOnboarding = async () => {
    if (!companyDoc || !authContext.authentication) return;

    setIsUnlockingOnboarding(true);
    try {
      const result = await unlockOnboarding({ company_id: companyDoc.id }, authContext.authentication);

      message.success(
        result.previous_onboarding_date
          ? `Date rouverte, l'onboarding du ${dayjs(result.previous_onboarding_date).format('DD/MM/YYYY')} est à reposer`
          : 'Date rouverte'
      );

      // The stored date is gone: a draft typed before the unlock would keep showing it
      setOnboardingDraft(null);
      companiesContext.fetchAndSelectById(companyDoc.id);
    } catch (e: unknown) {
      message.error((e as { message?: string })?.message || 'Une erreur est survenue');
    }
    setIsUnlockingOnboarding(false);
  };

  /**
   * A bank transfer landed on our account: Stripe never saw it, so the invoice has to be closed
   * by hand. Stripe then emits `invoice.paid` and the rest follows on its own.
   */
  const handleMarkInvoicePaid = async (invoiceId: string) => {
    if (!companyDoc || !authContext.authentication) return;

    setSettlingInvoiceId(invoiceId);
    try {
      await markInvoicePaidOutOfBand({ company_id: companyDoc.id, invoice_id: invoiceId }, authContext.authentication);
      message.success('Facture marquée réglée par virement');
      setUnpaidInvoices((oPrevious) => oPrevious
        ? { ...oPrevious, invoices: oPrevious.invoices.filter((oInvoice) => oInvoice.id !== invoiceId) }
        : oPrevious);
    } catch (e: unknown) {
      message.error((e as { message?: string })?.message || 'Une erreur est survenue');
    }
    setSettlingInvoiceId(null);
  };

  const handleSubmit = async () => {
    if (
      !companyDoc ||
      !authContext.authentication ||
      !newCustomerId.trim()
    )
      return;

    setSubmitting(true);
    try {
      const result = await linkStripeSubscription(
        {
          company_id: companyDoc.id,
          new_stripe_customer_id: newCustomerId.trim(),
          delete_old_stripe_customer: deleteOldCustomer,
        },
        authContext.authentication
      );

      if (result.created_subscriptions.length > 0) {
        message.success(
          `Client Stripe réassigné — ${result.created_subscriptions.length} abonnement(s) interne(s) créé(s)`
        );
      } else {
        message.success(
          result.message ||
          'Client Stripe réassigné (aucune subscription active trouvée)'
        );
      }

      setIsStripeModalOpen(false);
      companiesContext.fetchAndSelectById(companyDoc.id);
    } catch (e: unknown) {
      message.error(
        (e as { message?: string })?.message || 'Une erreur est survenue'
      );
    }
    setSubmitting(false);
  };

  // Only Stripe knows which invoices are still open, so they are fetched rather than mirrored
  useEffect(() => {
    const sCompanyId = companyDoc?.id;
    const sStripeId = (companyDoc as IExtendedCompany)?.id_stripe;
    if (!sCompanyId || !sStripeId || !authContext.authentication) return;

    let bCancelled = false;
    getUnpaidInvoices(sCompanyId, authContext.authentication)
      .then((aInvoices) => { if (!bCancelled) setUnpaidInvoices({ companyId: sCompanyId, invoices: aInvoices }); })
      .catch(() => { if (!bCancelled) setUnpaidInvoices({ companyId: sCompanyId, invoices: [] }); });

    return () => { bCancelled = true; };
  }, [companyDoc, authContext.authentication]);

  const isBusy = companyEditionFormContext.status === ContextStatus.LOADING;

  // Mirror of the Stripe subscription, written by the webhooks and realigned nightly: read-only
  // everywhere, since editing it here would only make the two disagree until the next sync.
  const billing = companyEditionFormContext.wipCompany?.billing;

  // Moving the date shifts the billing cycle: out of reach once the onboarding has happened
  const isOnboardingPending = billing?.onboarding_pending === true;

  // What the picker shows and what "Appliquer" sends, always the same value
  const onboardingValue = onboardingDraft?.companyId === companyDoc?.id
    ? onboardingDraft.value
    : (billing?.onboarding_date ? dayjs(billing.onboarding_date) : null);

  // Unlocking erases the date, so this warning is the last place the previous onboarding is
  // named. A company already served is the delicate case: re-applying a date suspends its
  // collection until that day and moves every engagement with it.
  const onboardingUnlockWarning = billing?.onboarding_date
    ? `Le service a démarré le ${dayjs(billing.onboarding_date).format('DD/MM/YYYY')} : débloquer efface cette date. Appliquer une nouvelle date suspendra la facturation Stripe et décalera les engagements.`
    : "Cette société facture normalement et n'a jamais eu d'onboarding. Poser une date suspendra sa facturation Stripe jusqu'à ce jour-là, puis son cycle repartira de là.";

  // What the recap shows for one number: the correction when there is one, the proposal otherwise
  const commitmentValueFor = (oMove: CommitmentMove) => {
    const iValue = oMove.subscription_id in commitmentEdits
      ? commitmentEdits[oMove.subscription_id]
      : oMove.proposed;
    return iValue ? dayjs(iValue) : null;
  };

  const readOnlyDate = (timestamp?: number | null) => (
    <DatePicker
      readOnly
      style={{ ...READ_ONLY_INPUT_STYLE, pointerEvents: 'none', cursor: 'not-allowed' }}
      value={timestamp ? dayjs(timestamp) : null}
      format="DD/MM/YYYY HH:mm"
      placeholder="Aucune"
    />
  );

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin].includes(
      authContext.authentication?.role
    );

  return companyEditionFormContext.wipCompany === null ? (
    <Card loading />
  ) : (
    <Flex vertical gap={16}>
      {(billing?.live_subscriptions ?? 1) > 1 && (
        <Alert
          type="error"
          showIcon
          banner
          message="Plusieurs abonnements Stripe actifs"
          description={
            <Flex vertical gap={8}>
              <span>
                <Typography.Text strong>{billing?.live_subscriptions} abonnements</Typography.Text> sont actifs
                sur ce client Stripe : la société est prélevée autant de fois, et le panneau de facturation
                ci-dessous ne décrit que celui retenu.
              </span>
              <Flex vertical>
                <Typography.Text strong>Action à faire :</Typography.Text>
                <span>- Résilier les abonnements en trop dans Stripe</span>
                <span>- Attendre la prochaine synchronisation à 5h du matin</span>
              </Flex>
            </Flex>
          }
        />
      )}
      {billing?.dunning_started_at && (
        <Alert
          type="warning"
          showIcon
          banner
          message="Facture d'abonnement impayée"
          description={
            <Flex vertical gap={8}>
              <span>
                Impayée depuis le <Typography.Text strong>{dayjs(billing.dunning_started_at).format('DD/MM/YYYY')}</Typography.Text>.
                Les crédits de tous les numéros de cette société ne se rechargent plus.
              </span>
              <Flex vertical>
                <Typography.Text strong>Action à faire :</Typography.Text>
                <span>- Faire régler la facture chez Stripe</span>
                <span>- Ou, si l&apos;argent est arrivé autrement, « Réglée par virement » dans « Factures impayées » ci-dessous</span>
                <span>- Dans les deux cas les recharges repartent la nuit suivante, les mois impayés ne sont pas rattrapés</span>
              </Flex>
            </Flex>
          }
        />
      )}
      <FormCard
        title={companyEditionFormContext.currentId}
        collectionName={FirestoreCollections.companies}
        onDeleted={
          isReadOnly ? undefined :
            () => companyEditionFormContext.submitDelete()
              .then(() => {
                message.success('Entreprise supprimée avec succès');
                navigate(COMPANIES_PAGE_PATH);
              })
              .catch(() => message.error('Erreur lors de la suppression de l\'entreprise'))
        }
        onUpdated={
          isReadOnly ? undefined : () => companyEditionFormContext.submitUpdate()
            .then(() => {
              message.success('Entreprise mise à jour avec succès');
              companiesContext.fetchAndSelectById(companyDoc.id);
            })
            .catch(() => message.error('Erreur lors de la mise à jour de l\'entreprise'))
        }
        isUpdateLoading={isBusy}
        isDeleteLoading={isBusy}
        headerActions={
          (isReadOnly || !isDemoAccount) ? undefined : [
            <Button
              key="create-demo-account"
              type="primary"
              icon={<PlusOutlined style={{ fontSize: "1rem" }} />}
              style={{ marginRight: '1rem' }}
              onClick={toggleCreateDemoAccountModal}
            >
              Compte de démo
            </Button>
          ]
        }
      >
        <Form layout="vertical" requiredMark={false}>
          <Form.Item label="Notes" layout="vertical">
            <Input.TextArea
              value={companyEditionFormContext.wipCompany.notes}
              onChange={(e) => companyEditionFormContext.onNotesChange(e.target.value)}
              placeholder="Notes internes sur cette entreprise (contexte commercial, interlocuteurs, points d'attention...)"
              rows={3}
            />
          </Form.Item>
          <FormContainer>
            <Form.Item label="ID" extra="id">
              <Input
                readOnly
                disabled={isBusy}
                style={READ_ONLY_INPUT_STYLE}
                value={companyEditionFormContext.wipCompany.id}
              />
            </Form.Item>

            <Form.Item
              label={<>
                ID Stripe
                <Button
                  className={styles.stripeLinkButton}
                  size='small'
                  type="link"
                  icon={<LinkOutlined />}
                  onClick={handleOpenStripeModal}
                >
                  Réassigner
                </Button>
                <Button
                  className={styles.stripeLinkButton}
                  size='small'
                  type="link"
                  icon={<ExportOutlined />}
                  onClick={() => window.open((companiesContext.selected as IExtendedCompany)?.customer_url, '_blank')}
                >
                  Voir
                </Button>
              </>}
              extra="id_stripe"
            >
              <Flex style={{ position: 'relative' }}>
                <Input
                  prefix={<StripeIcon className={styles.stripeIcon} aria-hidden />}
                  readOnly={!isEditingIdStripe}
                  disabled={isBusy}
                  style={{ ...(isEditingIdStripe ? {} : READ_ONLY_INPUT_STYLE), paddingRight: "2rem" }}
                  value={companyEditionFormContext.wipCompany.id_stripe ? companyEditionFormContext.wipCompany.id_stripe : !isEditingIdStripe ? 'Non défini' : ''}
                  onChange={(e) => {
                    companyEditionFormContext.onIdStripeChange(e.target.value);
                  }}
                />
                <Popconfirm
                  title="Attention !"
                  description="Ne modifiez ce champ que si vous savez ce que vous faites."
                  okText="Modifier"
                  cancelText="Annuler"
                  onConfirm={() => setIsEditingIdStripe(true)}
                  disabled={isEditingIdStripe}

                >
                  <Button type='link' icon={<EditOutlined />} onClick={() => isEditingIdStripe ? setIsEditingIdStripe(false) : undefined} style={{ position: 'absolute', right: 0, top: 0 }} />
                </Popconfirm>
              </Flex>
            </Form.Item>

            <Form.Item
              required
              label="Nom"
              validateStatus={handleInputError(
                companyEditionFormContext.wipCompany.name,
                companyEditionFormContext.status
              )}
              help={handleErrorInputMessage(
                companyEditionFormContext.wipCompany.name,
                companyEditionFormContext.status
              )}
              extra="name"
            >
              <Input
                disabled={isBusy}
                readOnly={isReadOnly}
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                onChange={(e) =>
                  companyEditionFormContext.onNameChange(e.target.value)
                }
                value={companyEditionFormContext.wipCompany.name}
                status={companyEditionFormContext.wipCompany.name === '' ? 'error' : ''}
              />
            </Form.Item>

            <Form.Item label="Date de création" extra="created_at">
              <DatePicker
                readOnly
                style={{ ...READ_ONLY_INPUT_STYLE, pointerEvents: 'none', cursor: 'not-allowed' }}
                value={dayjs(companyEditionFormContext.wipCompany.created)}
                format="DD/MM/YYYY"
              />
            </Form.Item>

            <Form.Item label="Dernière modification" extra="updated">
              {readOnlyDate(companyEditionFormContext.wipCompany.updated)}
            </Form.Item>

            <Form.Item label="SIRET" extra="siret">
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.siret || 'Aucun'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Clé API" help="Clé de l'entreprise. Reportée en clé API partenaire sur les abonnements créés par l'API Partner." />}
              extra="api_key"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.api_key || 'Aucune'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Inscription incomplète" help="L'entreprise vient d'un tunnel d'inscription qui n'est pas allé à son terme. Elle est reprise si la personne recommence." />}
              extra="incomplete"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.incomplete ? 'Oui' : 'Non'} />
            </Form.Item>

          </FormContainer>

          <FormSectionTitle title="Partenaire" />
          <FormContainer>
            <Form.Item
              label={<FormLabelWithHelp label="Système partenaire" help="CRM d'origine de l'entreprise. Tout abonnement créé ici en hérite, avec l'identifiant et la clé ci-dessous, et est traité par la synchro CRM de ce partenaire." />}
              extra="partner_system"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.partner_system || 'Aucun'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Identifiant partenaire" help="Identifiant de l'agence chez le partenaire : agency_id chez Apimo, manufacturer_id chez Orisha." />}
              extra="partner_uid"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.partner_uid || 'Aucun'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Clé API partenaire" help="Clé propre à l'agence, lue par la synchro CRM. Septeo uniquement." />}
              extra="partner_credentials.api_key"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.partner_credentials?.api_key || 'Aucune'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Site ID" help="Identifiant du site chez le partenaire. Immofacile uniquement." />}
              extra="partner_credentials.site_id"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={companyEditionFormContext.wipCompany.partner_credentials?.site_id || 'Aucun'} />
            </Form.Item>
          </FormContainer>

          <FormSectionTitle title="Facturation Stripe" />
          <FormContainer>
            <Form.Item
              label={<FormLabelWithHelp label="Abonnement Stripe" help="L'abonnement Stripe de la société. Un numéro y vaut une quantité." />}
              extra="billing.stripe_subscription_id"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={billing?.stripe_subscription_id || 'Aucun'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Statut" help="Statut de l'abonnement Stripe." />}
              extra="billing.status"
            >
              <Input readOnly style={READ_ONLY_INPUT_STYLE} value={billing?.status || 'Aucun'} />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Périodicité" help="Mensuel ou annuel. Les crédits se rechargent tous les mois dans les deux cas." />}
              extra="billing.interval"
            >
              <Input
                readOnly
                style={READ_ONLY_INPUT_STYLE}
                value={billing?.interval ? billingIntervalAsString(billing.interval) : 'Aucune'}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Début de période" help="Début de la période payée en cours chez Stripe. C'est aussi le jour d'ancrage du cycle de facturation." />}
              extra="billing.period_start"
            >
              {readOnlyDate(billing?.period_start)}
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Fin de période" help="Fin de la période payée, donc la prochaine facturation." />}
              extra="billing.period_end"
            >
              {readOnlyDate(billing?.period_end)}
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Résiliation programmée" help="Date où Stripe coupera l'abonnement. Vide si rien n'est prévu." />}
              extra="billing.cancel_at"
            >
              {readOnlyDate(billing?.cancel_at)}
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Fin d'essai" help="Fin de l'essai Stripe. Vide si pas d'essai." />}
              extra="billing.trial_end"
            >
              {readOnlyDate(billing?.trial_end)}
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Date d'onboarding" help="Jour où le service démarre. La période payée est décalée pour commencer ce jour-là, et l'engagement suit. Figée une fois l'onboarding passé, et sur les sociétés d'avant sa mise en place : « Débloquer » la rouvre." />}
              extra="billing.onboarding_date"
            >
              <Flex gap={8}>
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={isReadOnly || isSavingOnboarding || !isOnboardingPending}
                  format="DD/MM/YYYY"
                  value={onboardingValue}
                  onChange={(date) => setOnboardingDraft({ companyId: companyDoc.id, value: date })}
                  // A past day would start the service retroactively and shift the cycle backwards
                  disabledDate={(current) => current.isBefore(dayjs().startOf('day'))}
                  placeholder="Aucune"
                />
                {isOnboardingPending ? (
                  // No confirmation here: the recap that opens next is the confirmation, and it
                  // is the only place where the engagement moves can be read before they land.
                  <Button
                    type="primary"
                    disabled={isReadOnly}
                    loading={isSavingOnboarding}
                    onClick={handlePreviewOnboardingDate}
                  >
                    Appliquer
                  </Button>
                ) : (
                  <Popconfirm
                    title="Rouvrir la date d'onboarding ?"
                    description={<div style={{ maxWidth: 340 }}>{onboardingUnlockWarning}</div>}
                    okText="Débloquer"
                    cancelText="Annuler"
                    onConfirm={handleUnlockOnboarding}
                  >
                    <Button icon={<UnlockOutlined />} disabled={isReadOnly} loading={isUnlockingOnboarding}>
                      Débloquer
                    </Button>
                  </Popconfirm>
                )}
              </Flex>
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Impayé depuis" help="Jour où une facture d'abonnement a échoué. Les crédits ne se rechargent plus tant qu'elle n'est pas réglée. Vide : rien à recouvrer." />}
              extra="billing.dunning_started_at"
            >
              {readOnlyDate(billing?.dunning_started_at)}
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Dernière synchro" help="Dernière écriture depuis Stripe : webhook ou job de nuit." />}
              extra="billing.synced_at"
            >
              {readOnlyDate(billing?.synced_at)}
            </Form.Item>

            <Form.Item
              label={<FormLabelWithHelp label="Prochaine date de facturation" help="Copie de la fin de période, lue par les fronts." />}
              extra="next_billing_date"
            >
              {readOnlyDate(companyEditionFormContext.wipCompany.next_billing_date)}
            </Form.Item>

            <Form.Item
              label={<>
                ID de la dernière facture payée
                {(companiesContext.selected as IExtendedCompany)?.last_paid_invoice_id &&
                  <Button
                    className={styles.stripeLinkButton}
                    size='small'
                    type="link"
                    icon={<ExportOutlined />}
                    onClick={() => window.open((companiesContext.selected as IExtendedCompany)?.last_paid_invoice_url, '_blank')}
                  >
                    Voir
                  </Button>}
              </>}
              extra="last_paid_invoice_id"
            >
              <Input
                readOnly
                style={{ ...READ_ONLY_INPUT_STYLE, pointerEvents: 'none', cursor: 'not-allowed' }}
                value={(companiesContext.selected as IExtendedCompany)?.last_paid_invoice_id}
              />
            </Form.Item>

            {unpaidInvoices?.companyId === companyDoc?.id && unpaidInvoices.invoices.length > 0 && (
              <Form.Item
                label={<FormLabelWithHelp label="Factures impayées" help="Factures émises et non réglées chez Stripe. « Réglée par virement » clôt la facture sans rien prélever : à n'utiliser que si l'argent est bien arrivé sur le compte. Irréversible." />}
              >
                <Flex vertical gap={8}>
                  {unpaidInvoices.invoices.map((oInvoice) => (
                    <Flex key={oInvoice.id} align="center" justify="space-between" gap={8}>
                      <span>
                        {oInvoice.number ?? oInvoice.id} — {(oInvoice.amount_due / 100).toFixed(2)} {oInvoice.currency.toUpperCase()}
                        <span style={{ color: '#8c8c8c' }}> · émise le {dayjs(oInvoice.created).format('DD/MM/YYYY')}</span>
                      </span>
                      <Space>
                        {oInvoice.hosted_invoice_url && (
                          <Button size="small" type="link" icon={<ExportOutlined />} onClick={() => window.open(oInvoice.hosted_invoice_url as string, '_blank')}>
                            Voir
                          </Button>
                        )}
                        <Popconfirm
                          title="Marquer réglée par virement ?"
                          description="La facture sera close sans prélèvement, et l'abonnement prolongé comme si la carte était passée. Irréversible."
                          okText="Marquer réglée"
                          cancelText="Annuler"
                          onConfirm={() => handleMarkInvoicePaid(oInvoice.id)}
                        >
                          <Button size="small" disabled={isReadOnly} loading={settlingInvoiceId === oInvoice.id}>
                            Réglée par virement
                          </Button>
                        </Popconfirm>
                      </Space>
                    </Flex>
                  ))}
                </Flex>
              </Form.Item>
            )}
          </FormContainer>
        </Form>
      </FormCard>

      <Modal
        title={onboardingPreview?.onboardingDate
          ? `Démarrage au ${dayjs(onboardingPreview.onboardingDate).format('DD/MM/YYYY')}`
          : "Retirer la date d'onboarding"}
        open={onboardingPreview !== null}
        onCancel={() => setOnboardingPreview(null)}
        onOk={handleConfirmOnboardingDate}
        okText="Confirmer"
        cancelText="Annuler"
        okButtonProps={{ loading: isSavingOnboarding }}
        width={780}
      >
        <Space direction="vertical" style={{ width: '100%', maxHeight: '60vh', overflowY: 'auto' }} size="middle">
          <div style={{ fontSize: 12, color: '#888' }}>
            {onboardingPreview?.onboardingDate
              ? "La période déjà payée repartira de cette date et la facturation Stripe est suspendue jusque-là. Chaque engagement se déplace du même nombre de jours que le démarrage ; une date corrigée ici est écrite telle quelle."
              : 'La facturation Stripe reprend sur le cycle en cours, et chaque engagement revient au jour où son numéro a commencé à être servi.'}
          </div>

          {onboardingPreview?.moves.length === 0
            ? <div>Aucun numéro actif sur cette société.</div>
            : onboardingPreview?.moves.map((oMove, i) => (
              <React.Fragment key={oMove.subscription_id}>
                {i > 0 && <Divider style={{ margin: '0' }} />}
                <Flex align="center" justify="space-between" gap={16}>
                  <Flex vertical>
                    <Typography.Text strong>{oMove.name || oMove.sender || oMove.subscription_id}</Typography.Text>
                    <Typography.Text type="secondary">
                      {oMove.current ? `engagement actuel : ${dayjs(oMove.current).format('DD/MM/YYYY')}` : 'aucun engagement actuel'}
                    </Typography.Text>
                  </Flex>
                  <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="Aucun"
                    value={commitmentValueFor(oMove)}
                    // Nobody engages in the past: the engagement opens the day the service does.
                    // No floor when the date is being dropped — the service then goes back to the
                    // day each number started, which is behind us for a client already served.
                    disabledDate={(current) => onboardingPreview?.onboardingDate
                      ? current.isBefore(dayjs(onboardingPreview.onboardingDate), 'day')
                      : false}
                    onChange={(date) => setCommitmentEdits((oPrevious) => ({
                      ...oPrevious,
                      [oMove.subscription_id]: date ? date.startOf('day').valueOf() : null,
                    }))}
                  />
                </Flex>
              </React.Fragment>
            ))}
        </Space>
      </Modal>

      <Modal
        title="Réassigner un client Stripe"
        open={isStripeModalOpen}
        onCancel={() => setIsStripeModalOpen(false)}
        onOk={handleSubmit}
        okText="Réassigner"
        cancelText="Annuler"
        okButtonProps={{ disabled: !newCustomerId.trim(), loading: submitting }}
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Nouveau Client Stripe
            </label>
            <Input
              placeholder="cus_xxxxxxxxxxxxxxxx"
              value={newCustomerId}
              onChange={(e) => setNewCustomerId(e.target.value)}
            />
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Le client Stripe actuel ({companyDoc.id_stripe || 'aucun'}) sera
              remplacé. Si le nouveau client possède une subscription stripe
              active, les subscriptions et numéros seront automatiquement
              créés.
            </div>
          </div>

          {companyDoc.id_stripe && (
            <Checkbox
              checked={deleteOldCustomer}
              onChange={(e) => setDeleteOldCustomer(e.target.checked)}
            >
              Supprimer l'ancien client Stripe ({companyDoc.id_stripe}) pour
              éviter un orphelin
            </Checkbox>
          )}
        </Space>
      </Modal>

      <Modal
        title={`Créer un compte de démonstration pour ${companyEditionFormContext.wipCompany.name}`}
        open={isCreateDemoAccountModalOpen}
        onCancel={toggleCreateDemoAccountModal}
        footer={null}
        width={"75vw"}
        forceRender
      >
        {isCreateDemoAccountModalOpen ?
          <DemoAccountForm
            companyId={companyEditionFormContext.wipCompany.id}
            onCancel={toggleCreateDemoAccountModal}
          />
          : null
        }
      </Modal>
    </Flex>
  );
};

export default CompanyDetails;
